import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getDefaultLang } from "@cervval.js/common/src/i18n/languages";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { NOW, TODAY, TsSimpleDuration, UnixTimestamp, UnixTimestampInterval, add, beginOf, format, isAgendaMode, subtract, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { StringTools } from "@cervval.js/common/tools";
import Hammer from "hammerjs";
import { PopupContainer } from "../../../editors/editorsWithPicker/editorWithPicker";
import { CjsDatePicker, DatePickerOptions } from "../../../pickers/datePicker/datePicker";
import { CjsAgendaSelector } from "../../agenda/agendaSelector/agendaSelector";
import { PopupPosition, PopupValidationMode, showPopup } from "../../showPopup/showPopup";
import { I18nType, i18n } from "./i18n/i18n";

ensureLoaded(CjsDatePicker, CjsAgendaSelector);

const DEFAULT_DURATION: TsSimpleDuration = { unit: "week", unitCount: 1 };

/** Default units of duration, none <=> no limit applied */
const DEFAULT_NUMBER_UNITS = "none";

export interface DateNavigationOptions {

    /** 
     * Number of duration interval, used to set min/max dates
     * Default: none <=> no interval limits
     */
    nbUnits: number | "none";

    /** Unit of duration, step of dateNavigation */
    durationUnit: TsSimpleDuration;

    /** Add a pill on given dates, give information about element on this date */
    datePickerHighlightDates?: UnixTimestamp[];

    popupMode: PopupValidationMode;

    title?: Partial<{
        /**
         *Prefix on navigation display
         * ! Exclusive with centralBtnTitle 
         */
        prefixText: string,

        /**
         * Suffix on navigation display
         * ! Exclusive with centralBtnTitle 
         */
        suffixText: string,

        /** 
         * Text display on button
         * ! Exclusive with prefix/suffixText 
         */
        centralBtnTitle: (selectedDate: UnixTimestamp) => string
    }>;

    /** Will instantiate a mode selector if true, 
     * Selected mode will override duration Unit
     * only used in constructor
     */
    withAgendaSelector?: boolean;

    /** Disable central button */
    disableCentralButton?: boolean;

    /** Enable date-pills */
    enableDatePills?: boolean;

    /** Enable today reset */
    enableTodayReset?: boolean;

    /** Custom callback when user click on central button */
    customCallbackOnCentralBtnClick?: () => void;
}

export const defaultDateNavigationOptions: DateNavigationOptions = {

    /** 
     * Number of duration interval, used to set min/max dates
     * Default: none <=> no interval limits
     */
    nbUnits: DEFAULT_NUMBER_UNITS,

    /** Unit of duration, step of dateNavigation */
    durationUnit: DEFAULT_DURATION,

    popupMode: "default"
};


/**
 * Date navigation component
 * All pages using this component must call installHammerListeners() in page show and uninstallHammerListeners() in page hide
 */
@CustomElement({
    name: "cjs-date-navigation",
    style: require("./dateNavigation.scss"),
    template: require("./dateNavigation.html"),
    elementCssClasses: [FLEX_CONTAINER],
    i18n
})
export class CjsDateNavigation extends WebComponent<I18nType> {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @TypedHtmlElements()
    public readonly components!: {
        dateNavigationContainer: HTMLDivElement,
        leftBtn: HTMLButtonElement,
        todayResetBtn: HTMLButtonElement,
        todayResetBtnLabel: HTMLLabelElement,
        rightBtn: HTMLButtonElement,
        centralBtn: HTMLButtonElement
    };

    @TypedWebComponentOptions(defaultDateNavigationOptions)
    public readonly options!: WebComponentOptions<DateNavigationOptions>;

    /** Mobile start scroll */
    @Event(NoBubblesEventInit)
    public readonly mobileStartScrollEvent!: EventHandler;

    /** Mobile finish scroll */
    @Event(NoBubblesEventInit)
    public readonly mobileFinishScrollEvent!: EventHandler;

    /** Date picker */
    public datePicker = new CjsDatePicker({ displayOtherMonthsDays: true });

    /** Current date selected */
    private _selectedDate = NOW();

    /** Hammer events manager */
    private _hammerManager: HammerManager | undefined;

    /** Min date allowed for navigation to */
    private _minDate?: UnixTimestamp;

    /** Max date allowed for navigation to */
    private _maxDate?: UnixTimestamp;

    /** Popup container for date picker */
    private _popup: PopupContainer | null = null;

    /** Selected used to select mode */
    public readonly modeSelector?: CjsAgendaSelector;

    /** Key of "dateChangedAndNeedRefreshData" event from date navigation component */
    @Event(NoBubblesEventInit)
    public selectedDateChangedEvent!: EventHandler<UnixTimestamp>;

    constructor(options?: Partial<DateNavigationOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);

        if (this.options.withAgendaSelector) {
            this.modeSelector = new CjsAgendaSelector();
            this.modeSelector.selector.components.smartSelectContainer.classList.add("btn", "date-nav-btn");
            setDisplay(this.modeSelector, Display.NONE);
            this.components.dateNavigationContainer.append(this.modeSelector);
        }
    }

    /**
     * When component is created
     */
    public override init(): void {
        this._selectedDate = beginOf(this._selectedDate, this.options.durationUnit.unit).uts;
        this._initListeners();
    }

    /**
     * Bind events
     */
    private _initListeners(): void {
        this.components.centralBtn.onclick = (e) => this._openDatePicker(e);
        this.components.leftBtn.onclick = () => this._subDurationUnitOnLeftBtnClick();
        this.components.rightBtn.onclick = () => this._addDurationUnitOnRightBtnClick();
        this.components.leftBtn.ariaLabel = this.i18n.leftBtn;
        this.components.rightBtn.ariaLabel = this.i18n.rightBtn;
        this.components.todayResetBtn.onclick = () => this.setSelectedDate(TODAY());
        this.components.todayResetBtnLabel.innerText = this.i18n.todayResetBtn;

        if (this.modeSelector) {
            this.modeSelector.selectedModeChangeEvent.on(e => this.options.setAndRefreshIfChanged({
                durationUnit: { unit: e.detail, unitCount: this.options.durationUnit.unitCount }
            }));
        }

        if (this.options.popupMode === "default") {
            // Date picker on date change event
            this.datePicker.valueChangeEvent.on(() => this._selectedDateChanged());
        } else {

            // Add listener on value change event of popupComponent (picker)
            this.datePicker.valueChangeEvent.on(() => {
                if (this._popup) {
                    // Close popup and enable toggleBtn
                    this._popup.removePopup("close");
                }
            });
        }
    }

    /**
     * Bind hammerJS events for mobile
     */
    public installHammerListeners(): void {
        if (this._hammerManager) { return; } // If already created, do not create it
        this._hammerManager = new Hammer(document.body, { inputClass: Hammer.TouchInput }); // To prevent swipe event on desktop
        this._hammerManager.get("swipe").set({ direction: Hammer.DIRECTION_HORIZONTAL });
        this._hammerManager.on("swiperight", (e: HammerInput) => {
            if (this._popup) { this._popup.removePopup("cancel"); }
            this._subDurationUnitOnLeftBtnClick();
        });
        this._hammerManager.on("swipeleft", (e: HammerInput) => {
            if (this._popup) { this._popup.removePopup("cancel"); }
            this._addDurationUnitOnRightBtnClick();
        });
        this._hammerManager.on("panmove panstart", () => {
            this.mobileStartScrollEvent.emit();
        });
        this._hammerManager.on("panend pancancel", () => {
            this.mobileFinishScrollEvent.emit();
        });
    }

    /**
     * Unbind HammerJS events for mobile
     */
    public uninstallHammerListeners(): void {
        if (this._hammerManager) {
            this._hammerManager.destroy();
            this._hammerManager = undefined;
        }
    }

    /**
     * Initialize the date navigation component
     */
    public initialize(params: Partial<DateNavigationOptions>, datePickerOptions?: Partial<DatePickerOptions>): void {

        let refreshDatePicker = false;
        this.options.setWithoutRefresh(params);

        // Check if nbUnits range is set , handle negative values
        if (this.options.nbUnits !== defaultDateNavigationOptions.nbUnits && this.options.nbUnits !== "none" && this.options.nbUnits > 0) {
            const computedDuration = this._getComputedDuration();
            this._minDate = subtract(this._selectedDate, computedDuration).uts;
            this._maxDate = add(this._selectedDate, computedDuration).uts;

            // Set limits on DatePicker
            this.datePicker.options.setWithoutRefresh({
                ranges: [{
                    start: this._minDate,
                    finish: this._maxDate
                }]
            });
            refreshDatePicker = true;
        }

        if (datePickerOptions) {
            this.datePicker.options.setWithoutRefresh(datePickerOptions);
            refreshDatePicker = true;
        }
        if (refreshDatePicker) {
            this.datePicker.refresh();
        }

        this.refresh();
    }

    /** Refresh component display */
    public override refresh(): void {
        if (this.modeSelector) {
            if (isAgendaMode(this.options.durationUnit.unit)) {
                this.modeSelector.options.setWithoutRefresh({ mode: this.options.durationUnit.unit });
                setDisplay(this.modeSelector, Display.FLEX);
            }
            else {
                setDisplay(this.modeSelector, Display.NONE);
            }
            this.modeSelector.refresh();
        }
        toggleVisible(this.components.todayResetBtn, this.options.enableTodayReset, Display.FLEX);

        this._updateNavigationDatesFromDatePickerRanges();

        if (this.options.title?.centralBtnTitle) {
            this.components.centralBtn.innerHTML = this.options.title.centralBtnTitle(this._selectedDate);
        } else {
            this.components.centralBtn.innerHTML = this.options.title?.prefixText ?? "" + " " + this._formatSelectedDate();
            if (this.options.title?.suffixText) {
                this.components.centralBtn.innerHTML += " " + this.options.title?.suffixText;
            }
        }

        // Toggle disabled month pick class
        toggleClass(this.components.centralBtn, Boolean(this.options.disableCentralButton), "disabled-btn");

        const customCb = this.options.customCallbackOnCentralBtnClick;
        if (customCb) {
            this.components.centralBtn.onclick = () => {
                if (this.options.disableCentralButton) { return; }
                customCb();
            };
        }
    }

    /** When date picker selected date changed */
    private _selectedDateChanged(): void {
        this.setSelectedDate(this.datePicker?.getValue() ?? NOW());
    }

    /**
     * Trigger date picker opening
     */
    private _openDatePicker(e: Event): void {
        if (this.options.disableCentralButton) { return; }

        const onHideCallBack = this.options.popupMode === "direct" ? this._selectedDateChanged.bind(this) : () => { };

        this.datePicker.setValue(this._selectedDate);
        this.datePicker.refresh();

        this._popup = showPopup({
            elementToShow: this.datePicker,
            parent: this.components.dateNavigationContainer,
            event: e,
            onPopupHide: () => onHideCallBack(),
            popupPosition: PopupPosition.AUTO
        });

        if (this.options.enableDatePills) {
            // Refresh date pills
            this._refreshDatePickerPills(this.datePicker);

            // Refresh date pills on datePicker navigation
            this.datePicker.calendarNavigationEvent.on(() => this._refreshDatePickerPills(this.datePicker));
        }
    }

    /** Refresh date pills on given datePicker */
    protected _refreshDatePickerPills(datePicker: CjsDatePicker): void {
        const datesSet = new Set(this.options.datePickerHighlightDates ?? []);
        datePicker.dayCells.forEach(cell => {
            if (!datesSet.has(cell.date.uts)) { return; }
            const pill = createHtmlElement("div", { cssClass: ["date-pill"] });
            cell.append(pill);
        });
    }

    /** Update the date navigation date range from the date picker range */
    private _updateNavigationDatesFromDatePickerRanges(): void {
        const ranges = this.datePicker.options.ranges;
        let min: UnixTimestamp | undefined;
        let max: UnixTimestamp | undefined;
        for (const range of ranges ?? []) {
            if (min == null || (range.start != null && range.start < min)) {
                min = range.start;
            }
            if (max == null || (range.finish != null && range.finish > max)) {
                max = range.finish;
            }
        }
        if (min != null) { this._minDate = min; }
        if (max != null) { this._maxDate = max; }
    }

    /**
     * Get selected date
     * @returns return the selected date
     */
    public getSelectedDate(): UnixTimestamp {
        return this._selectedDate;
    }

    /**
     * Set selected date
     */
    public setSelectedDate(newDateTs: UnixTimestamp, p?: { refresh?: boolean, doNotDispatchEvent?: boolean }): void {
        const oldDate = toDate(this._selectedDate);
        this._selectedDate = newDateTs;
        const newDate = toDate(newDateTs);

        let doNotFireEvent = false;
        if (this.options.durationUnit.unit === "year") {
            doNotFireEvent = oldDate.year === newDate.year;
        } else if (this.options.durationUnit.unit === "month") {
            doNotFireEvent = oldDate.yearAndMonth === newDate.yearAndMonth;
        } else if (this.options.durationUnit.unit === "week") {
            doNotFireEvent = oldDate.weekYearAndWeekNumber === newDate.weekYearAndWeekNumber;
        } else if (oldDate.yearAndOrdinal === newDate.yearAndOrdinal) {// Don't dispatch if same date is set (value don't change)
            doNotFireEvent = true;
        }

        if (!doNotFireEvent && !(p?.doNotDispatchEvent === true)) {
            this.selectedDateChangedEvent.emit(this._selectedDate);
        }

        if (p?.refresh !== false) {
            this.refresh();
        }
    }

    /** @returns current interval {start: _selectedDate, finish: _selectedDate + dayCount} */
    public getCurrentInterval(): UnixTimestampInterval {
        const start = this._selectedDate;
        const finish = toDate(this._selectedDate).add(this.options.durationUnit).uts;
        return { start, finish };
    }


    /**
     * Format the current selected date to a string which can be display for Humans
     * @returns date in string format
     */
    private _formatSelectedDate(): string {
        const selectedDate = toDate(this._selectedDate);

        if (this.options.durationUnit.unit === "year") {
            return String(selectedDate.year);
        } else if (this.options.durationUnit.unit === "month") {
            return StringTools.capitalizeFirstLetter(format(selectedDate, "MMMM"));
        } else if (this.options.durationUnit.unit === "week") {
            return (getDefaultLang() === "fr" ? "Semaine " : "Week ") + selectedDate.weekNumber;
        } else {
            return format(selectedDate, "dd MMMM yyyy");// TODO use locale dependent formats
        }
    }

    /**
     * Calculate the computed duration with unit and nb of units to get min and max allowed dates
     * @returns computed duration
     */
    private _getComputedDuration(): TsSimpleDuration {
        const nbUnits: number = this.options.nbUnits === "none" ? 1 : this.options.nbUnits;/** BGS, I put "1" for none, I suppose we should not call this function if nbUnits is none... */
        return { unit: this.options.durationUnit.unit, unitCount: this.options.durationUnit.unitCount * nbUnits };
    }

    /**
     * Add a duration unit to current selected date and refresh
     */
    private _addDurationUnitOnRightBtnClick(): void {
        const newDate = add(this._selectedDate, this.options.durationUnit);

        // Enable left btn
        this.components.leftBtn.disabled = false;

        if (this._maxDate == null || newDate.uts < this._maxDate) {

            // Enable right btn
            this.components.rightBtn.disabled = this._maxDate != null && newDate.yearAndOrdinal === toDate(this._maxDate).yearAndOrdinal;// disabled if same day

            // Set new value, refresh
            this.setSelectedDate(newDate.uts);
            this.refresh();
        } else if (this._selectedDate < this._maxDate) {

            // Enable right btn
            this.components.rightBtn.disabled = true;

            // Set max value, refresh
            this.setSelectedDate(this._maxDate);
            this.refresh();
        } else {
            // Disable right btn
            this.components.rightBtn.disabled = true;
        }

    }

    /**
     * Sub a duration unit to current selected date and refresh
     */
    private _subDurationUnitOnLeftBtnClick(): void {
        const newDate = subtract(this._selectedDate, this.options.durationUnit);

        // Enable right btn
        this.components.rightBtn.disabled = false;

        if (this._minDate == null || newDate.uts > this._minDate) {

            // Enable left btn
            this.components.leftBtn.disabled = this._minDate != null && newDate.yearAndOrdinal === toDate(this._minDate).yearAndOrdinal;// is same day

            // Set new value, refresh
            this.setSelectedDate(newDate.uts);
            this.refresh();
        } else if (this._selectedDate > this._minDate) {

            // Enable left btn
            this.components.leftBtn.disabled = true;

            // Set min value, refresh
            this.setSelectedDate(this._minDate);
            this.refresh();
        } else {

            // Disable left btn
            this.components.leftBtn.disabled = true;
        }
    }
}

